import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { MobileAudioProvider } from './audio/MobileAudioContext';
import { MobileMiniPlayer } from './audio/MobileMiniPlayer';
import { installDprClamp } from './dprClamp';
import {
  bindMobilePrefsLifecycle,
  mobilePrefsStore,
  useMobilePrefsSettled,
} from './mobilePrefsStore';
import { MobileSettingsSheet } from './MobileSettingsSheet';
import { MobileTabBar } from './MobileTabBar';
import { ViewGate } from '../components/ViewGate';
import {
  mobileTabById,
  tabIdFromPathname,
  type MobileTabId,
} from './mobileTabRegistry';
import { useForegroundResync } from './useForegroundResync';
import { useInstallPrompt } from './useInstallPrompt';
import './mobile.css';

/**
 * Full mobile shell: header + active tab + bottom bar.
 * Audio engines mount at shell level (MobileAudioProvider) so tab switches
 * do not tear down WebRTC / AudioContext.
 */
export function MobileWorkspace(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { canPrompt, isIos, promptInstall } = useInstallPrompt();
  const [showIosHint, setShowIosHint] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settled = useMobilePrefsSettled();
  const queryClient = useQueryClient();
  const { isConnected } = useWebSocket();

  const activeId: MobileTabId = useMemo(
    () => tabIdFromPathname(location.pathname),
    [location.pathname],
  );

  const tabDef = mobileTabById(activeId);
  const TabBody = tabDef.component;
  const onAudioTab = activeId === 'audio';

  const onTabChange = useCallback(
    (id: MobileTabId) => {
      const next = mobileTabById(id);
      if (location.pathname !== next.path) {
        navigate(next.path, { replace: false });
      }
    },
    [location.pathname, navigate],
  );

  const openAudio = useCallback(() => {
    navigate('/audio');
  }, [navigate]);

  useEffect(() => {
    void mobilePrefsStore.hydrate();
    return bindMobilePrefsLifecycle();
  }, []);

  // Cap DPR while the mobile shell is mounted; restore on unmount.
  useEffect(() => installDprClamp(), []);

  // iOS kills sockets and freezes timers in the background; the app's query
  // client has refetchOnWindowFocus disabled, so nothing else refreshes here.
  useForegroundResync(() => {
    void queryClient.invalidateQueries();
  });

  const onInstall = useCallback(() => {
    if (canPrompt) {
      void promptInstall();
      return;
    }
    if (isIos) setShowIosHint((v) => !v);
  }, [canPrompt, isIos, promptInstall]);

  return (
    <MobileAudioProvider>
      <div className="sr-mobile-shell">
        <header className="sr-mobile-header">
          <div className="sr-mobile-header__brand">FZN</div>
          <span
            className={
              isConnected ? 'sr-mobile-live' : 'sr-mobile-live sr-mobile-live--off'
            }
            title={isConnected ? 'Live data connected' : 'Data feed disconnected'}
          >
            <span className="sr-mobile-live__dot" aria-hidden="true" />
            LIVE
          </span>
          <div className="sr-mobile-header__actions">
            {(canPrompt || isIos) && (
              <button
                type="button"
                className="sr-mobile-header__btn"
                onClick={onInstall}
                title="Install app"
              >
                Install
              </button>
            )}
            <button
              type="button"
              className="sr-mobile-header__btn"
              onClick={() => setSettingsOpen(true)}
              aria-label="Settings"
              title="Settings"
            >
              ⚙
            </button>
          </div>
        </header>

        {settingsOpen && <MobileSettingsSheet onClose={() => setSettingsOpen(false)} />}

        {showIosHint && (
          <div className="px-3 py-2 text-xs text-gray-300 bg-dark-surface border-b border-dark-border">
            On iOS Safari: Share → <strong>Add to Home Screen</strong>
          </div>
        )}

        <main
          className={
            tabDef.fillContent
              ? 'sr-mobile-content sr-mobile-content--fill'
              : 'sr-mobile-content'
          }
        >
          {settled ? (
            tabDef.viewType ? (
              <ViewGate viewType={tabDef.viewType}>
                <TabBody />
              </ViewGate>
            ) : (
              <TabBody />
            )
          ) : (
            <div className="sr-mobile-loading" aria-busy="true">
              Loading…
            </div>
          )}
        </main>

        {!onAudioTab && <MobileMiniPlayer onOpenAudio={openAudio} />}

        <MobileTabBar active={activeId} onChange={onTabChange} />
      </div>
    </MobileAudioProvider>
  );
}
